/**
 * Feature definitions for Marketplace and Help Center.
 * Single source of truth for all features, plans, help content, and marketing copy.
 */

export interface Feature {
  slug: string;
  name: string;
  description: string;
  category: FeatureCategory;
  icon: string; // Lucide icon name
  minPlan: number; // 0=trial, 1=starter, 2=pro, 3=enterprise, 4=scale
  route?: string; // Link to the actual feature page
  highlight?: string; // One-line marketing hook
  whyItMatters: string[];
  howToUse: string[];
  tips: string[];
  relatedSlugs?: string[];
}

export type FeatureCategory =
  | 'getting-started'
  | 'client-intelligence'
  | 'financial-analysis'
  | 'ai-tools'
  | 'compliance'
  | 'automation'
  | 'reports'
  | 'integrations'
  | 'security'
  | 'smsf';

export const CATEGORIES: Record<FeatureCategory, { label: string; description: string; icon: string }> = {
  'getting-started': { label: 'Getting Started', description: 'Core features to begin your journey', icon: 'Rocket' },
  'client-intelligence': { label: 'Client Intelligence', description: 'Manage and analyse your clients', icon: 'Users' },
  'financial-analysis': { label: 'Financial Analysis', description: 'Deep dive into financial data', icon: 'BarChart3' },
  'ai-tools': { label: 'AI Tools', description: 'AI-powered analysis and predictions', icon: 'Sparkles' },
  'compliance': { label: 'Compliance & Tax', description: 'Stay compliant and optimise tax', icon: 'Shield' },
  'smsf': { label: 'SMSF', description: 'Self-Managed Super Fund management and compliance', icon: 'Shield' },
  'automation': { label: 'Automation', description: 'Automate repetitive tasks', icon: 'Zap' },
  'reports': { label: 'Reports & Dashboards', description: 'Generate insights and reports', icon: 'FileText' },
  'integrations': { label: 'Integrations', description: 'Connect your tools', icon: 'Link2' },
  'security': { label: 'Security', description: 'Enterprise-grade security', icon: 'Lock' },
};

export const PLAN_NAMES: Record<number, { name: string; color: string; bg: string }> = {
  0: { name: 'Free', color: 'text-zinc-500', bg: 'bg-zinc-100 dark:bg-zinc-800' },
  1: { name: 'Starter', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-950' },
  2: { name: 'Professional', color: 'text-[#5048e5]', bg: 'bg-[#5048e5]/10' },
  3: { name: 'Enterprise', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950' },
  4: { name: 'Scale', color: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-950' },
};

export const FEATURES: Feature[] = [
  // ==================== Getting Started ====================
  {
    slug: 'ai-chat',
    name: 'AI Knowledge Chat',
    description: 'Ask questions about your knowledge base and receive AI-powered answers complete with source citations. RoniDoc\'s AI searches across all your uploaded documents, ATO rulings, and the global knowledge base to deliver accurate, referenced responses in seconds. It is like having a senior researcher on call around the clock.',
    category: 'getting-started',
    icon: 'MessageSquare',
    minPlan: 0,
    route: '/panel/kb-chat',
    highlight: 'Your firm\'s AI brain, delivering instant answers from all your documents',
    whyItMatters: [
      'Stop searching through folders manually. Just ask a question and get an answer in seconds, saving you valuable billable time.',
      'Every answer comes with source citations so you can verify the information and feel confident sharing it with clients',
      'Works across all uploaded documents, ATO rulings, and the global knowledge base for comprehensive coverage',
      'Reduces research time by up to 80% compared to manual document searching, freeing you to focus on advisory work',
    ],
    howToUse: [
      'Navigate to [Chat](/panel/kb-chat) from the top menu',
      'Type your question in natural language, for example "What are the FBT exemptions for small businesses?"',
      'Review the AI response along with cited sources',
      'Click on any citation to view the original document section',
      'Use "Explain More" for deeper analysis of complex topics',
      'Toggle "Include Client Files" to search across all client documents too',
    ],
    tips: [
      'Be specific in your questions. For example, "What is the CGT discount for assets held over 12 months?" works better than "Tell me about CGT"',
      'Use follow-up questions in the same session to drill deeper into a topic',
      'Upload ATO rulings and practice statements to make the AI even more knowledgeable',
      'Select the data source (Documents, Xero, or Both) to control where answers come from',
    ],
    relatedSlugs: ['client-chat', 'document-upload', 'global-kb'],
  },
  {
    slug: 'client-chat',
    name: 'Client AI Chat',
    description: 'Chat with AI about a specific client, powered by their documents and Xero financial data. RoniDoc combines everything it knows about a client into a single conversational interface, so you can ask complex financial questions and get detailed, contextual answers instantly.',
    category: 'getting-started',
    icon: 'MessageSquare',
    minPlan: 0,
    route: '/panel/files/clients',
    highlight: 'Every client gets their own AI analyst',
    whyItMatters: [
      'Get instant answers about any client\'s financials, documents, and compliance status without switching between tools',
      'RoniDoc\'s AI combines Xero data with uploaded documents for comprehensive, multi-source analysis',
      'Ask complex questions like "Compare this year\'s revenue to last year and explain the variance" and get meaningful answers',
      'Reduces time spent preparing for client meetings from hours to minutes, so you walk in fully briefed every time',
    ],
    howToUse: [
      'Go to [Clients](/panel/files/clients) and select a client',
      'Click "Open Client Workspace" to enter the client file browser',
      'The AI chat panel appears on the right side',
      'Choose your data source: Documents only, Xero only, or Both',
      'Ask questions about the client\'s financials, compliance, or documents',
    ],
    tips: [
      'Upload key documents (tax returns, engagement letters, financial statements) for richer AI responses',
      'Connect Xero to automatically pull Balance Sheet, P&L, and Trial Balance data',
      'Ask for specific dates, such as "Show me the balance sheet as at 30 June 2025"',
      'The AI remembers context within a conversation, so build on previous questions for deeper analysis',
    ],
    relatedSlugs: ['ai-chat', 'xero-integration', 'document-upload'],
  },
  {
    slug: 'document-upload',
    name: 'Document Upload & Indexing',
    description: 'Upload PDF, Excel, Word, and CSV files, and RoniDoc\'s AI automatically indexes them for instant search and analysis. Every document you upload becomes part of your firm\'s growing intelligence layer, making your AI smarter with each file added.',
    category: 'getting-started',
    icon: 'Upload',
    minPlan: 0,
    route: '/panel/knowledge-base',
    highlight: 'Turn static files into searchable, AI-queryable knowledge',
    whyItMatters: [
      'Uploaded documents become instantly searchable by AI, transforming passive storage into active intelligence',
      'Supports PDF, DOCX, XLSX, CSV, TXT, and image files for maximum flexibility',
      'AI extracts and indexes content automatically, so no manual tagging or categorisation is needed',
      'Build a growing knowledge base that gets smarter with every upload, compounding your firm\'s expertise over time',
    ],
    howToUse: [
      'Go to [Knowledge Base](/panel/knowledge-base) or a Client Workspace',
      'Click Upload or drag-and-drop files',
      'Wait for processing (usually 10-30 seconds per document)',
      'Once indexed, the document is available for AI chat queries',
    ],
    tips: [
      'Upload ATO rulings, practice statements, and tax guides to supercharge your AI',
      'Use folders to organise documents by client, year, or type',
      'Name files descriptively. For example, "2025_Tax_Return_SmithCo.pdf" is far better than "doc1.pdf"',
      'Check the indexing status, because only "completed" documents are searchable by AI',
    ],
    relatedSlugs: ['ai-chat', 'client-chat', 'global-kb'],
  },
  {
    slug: 'global-kb',
    name: 'Global Knowledge Base',
    description: 'A shared knowledge layer across your entire firm, including ATO rulings, industry benchmarks, and best practices. RoniDoc pre-loads this with official ATO data for 95+ industries, and your team can enrich it further with firm-specific policies and procedures.',
    category: 'getting-started',
    icon: 'Library',
    minPlan: 0,
    route: '/panel/global-knowledge',
    highlight: 'ATO rulings and industry knowledge at your fingertips',
    whyItMatters: [
      'Pre-loaded with ATO benchmarks for 95+ industries, giving you a head start from day one',
      'Contains tax rulings, practice statements, and compliance guides to keep your team informed',
      'Shared across your entire firm, so you upload once and benefit everywhere',
      'AI uses this knowledge to provide accurate, regulation-aware answers in every conversation',
    ],
    howToUse: [
      'Browse categories in the [Global Knowledge Base](/panel/global-knowledge) page',
      'Search for specific topics or rulings',
      'Click any document to read its content',
      'Ask questions in [Chat](/panel/kb-chat) and the AI automatically references global knowledge',
    ],
    tips: [
      'The global KB is always included in AI chat responses, so there is no need to specify it separately',
      'Request your admin to upload firm-specific policies and procedures',
      'Use the ATO benchmark data to compare client performance against industry standards',
    ],
    relatedSlugs: ['ai-chat', 'ato-benchmarks', 'document-upload'],
  },

  // ==================== Client Intelligence ====================
  {
    slug: 'client-management',
    name: 'Client Management',
    description: 'Manage all your clients in one centralised hub with health scores, Xero connections, and document tracking. RoniDoc gives you a real-time overview of every client\'s status, so you can identify who needs attention and take action before small issues become big problems.',
    category: 'client-intelligence',
    icon: 'Users',
    minPlan: 0,
    route: '/panel/files/clients',
    highlight: 'Your entire client portfolio in one place',
    whyItMatters: [
      'See all clients at a glance with health scores and activity status for complete visibility',
      'Track document completeness and compliance readiness per client to stay ahead of deadlines',
      'Auto-match clients to Xero organisations by ABN/ACN, eliminating manual linking',
      'Identify at-risk clients before problems escalate, giving you time to intervene proactively',
    ],
    howToUse: [
      'Go to [Clients](/panel/files/clients) from the top menu',
      'Add clients manually or connect [Xero](/panel/integrations/xero) to auto-import',
      'Click any client to access their workspace',
      'Upload documents, chat with AI, and run compliance checks',
    ],
    tips: [
      'Fill in ABN for each client, as it enables automatic Xero matching',
      'Set the industry field to get ATO benchmark comparisons',
      'Use the health score to prioritise which clients need attention',
    ],
    relatedSlugs: ['xero-integration', 'compliance-checker', 'client-chat'],
  },
  {
    slug: 'firm-dashboard',
    name: 'Firm Dashboard',
    description: 'Get a bird\'s eye view of your entire practice with KPIs, activity feeds, and client health metrics all in one place. RoniDoc\'s dashboard gives practice managers the visibility they need to make informed decisions without opening individual client files.',
    category: 'client-intelligence',
    icon: 'BarChart3',
    minPlan: 2,
    route: '/panel/firm/dashboard',
    highlight: 'See your entire practice performance at a glance',
    whyItMatters: [
      'Monitor practice-wide KPIs without opening individual clients, saving hours of administrative work',
      'Track document upload activity and AI usage across the firm to understand team engagement',
      'Spot clients that need attention with the health score overview, so nothing slips through the cracks',
      'Xero connection health monitoring catches expired tokens before they block data access',
    ],
    howToUse: [
      'Navigate to [Overview](/panel/firm/dashboard) from the top menu',
      'Review the KPI cards at the top for total clients, documents, and Xero connections',
      'Check the client table for health scores and recent activity',
      'Review Xero connection status to ensure all integrations are healthy',
    ],
    tips: [
      'Check the dashboard weekly to catch any declining health scores early',
      'Use the recent activity feed to see what your team has been working on',
      'The Xero health section warns you when tokens need refreshing',
    ],
    relatedSlugs: ['client-management', 'compliance-checker', 'deadlines'],
  },
  {
    slug: 'multi-client-query',
    name: 'Ask All Clients',
    description: 'Query across all clients simultaneously to find patterns, compare financials, and spot outliers in your portfolio. With RoniDoc, a single question can surface insights that would otherwise take hours of manual spreadsheet analysis to uncover.',
    category: 'client-intelligence',
    icon: 'MessageSquare',
    minPlan: 3,
    route: '/panel/multi-client',
    highlight: 'Ask one question, get answers across your entire portfolio',
    whyItMatters: [
      'Find patterns across your client base that would take hours to discover manually',
      'Compare financial metrics across similar clients instantly for benchmarking and advisory',
      'Identify clients at risk of ATO audit based on benchmark deviations before lodgement',
      'Perfect for preparing board reports or practice management reviews with data-backed insights',
    ],
    howToUse: [
      'Go to [Ask All Clients](/panel/multi-client) from the top menu',
      'Type your question, for example "Which clients have declining revenue?"',
      'AI searches across all connected clients and their Xero data',
      'Review the aggregated response with per-client breakdowns',
    ],
    tips: [
      'Ask comparative questions: "Which clients have the highest expense ratios?"',
      'Use for tax planning season: "Which clients might benefit from the instant asset write-off?"',
      'Great for identifying cross-selling opportunities across your portfolio',
    ],
    relatedSlugs: ['client-chat', 'compliance-checker', 'firm-dashboard'],
  },

  // ==================== Financial Analysis ====================
  {
    slug: 'xero-integration',
    name: 'Xero Integration',
    description: 'Connect Xero to automatically pull Balance Sheet, P&L, Trial Balance, and Aged Reports into RoniDoc. Financial data flows directly from Xero into your AI-powered workspace, eliminating manual data entry and ensuring your analysis is always based on the latest figures.',
    category: 'financial-analysis',
    icon: 'Link2',
    minPlan: 1,
    route: '/panel/integrations/xero',
    highlight: 'Live financial data flowing into your AI',
    whyItMatters: [
      'No more manual data entry, because financial reports pull directly from Xero in real time',
      'AI can analyse Balance Sheet, P&L, Trial Balance, and Aged Reports for deep financial insights',
      'Multi-org support lets you connect all your clients\' Xero organisations in one go',
      'Read-only access means RoniDoc never modifies your Xero data, keeping your ledger safe',
    ],
    howToUse: [
      'Go to [Settings > Integrations > Xero](/panel/integrations/xero)',
      'Click "Connect to Xero" and authorise access',
      'Select the organisations you want to connect',
      'Clients auto-match to Xero orgs by ABN/ACN/name',
      'Financial data is now available in [Client Chat](/panel/files/clients) and compliance tools',
    ],
    tips: [
      'Connect all orgs at once. You can select multiple on the Xero consent screen to save time.',
      'Set a lock date in Xero after finalising accounts to ensure API data matches published statements',
      'Use per-org disconnect to remove individual organisations without affecting others',
    ],
    relatedSlugs: ['client-chat', 'reconciliation', 'smart-comparison'],
  },
  {
    slug: 'reconciliation',
    name: 'Auto-Reconciliation Checker',
    description: 'Automatically compare Trial Balance against P&L and Balance Sheet to flag discrepancies before they become audit issues. RoniDoc\'s reconciliation engine runs intelligent checks across your Xero data and provides AI-suggested explanations for every mismatch it finds.',
    category: 'financial-analysis',
    icon: 'Scale',
    minPlan: 2,
    route: '/panel/intelligence/reconciliation',
    highlight: 'Catch reconciliation errors before they become audit issues',
    whyItMatters: [
      'Automatically compares TB against P&L and Balance Sheet from Xero for complete cross-validation',
      'Flags mismatches, rounding differences, and missing accounts with clear severity indicators',
      'Provides AI-suggested explanations for each discrepancy, accelerating your review process',
      'Saves hours of manual reconciliation work per client, letting you handle more clients in less time',
    ],
    howToUse: [
      'Navigate to [Reconciliation](/panel/intelligence/reconciliation) from the top menu',
      'Select a client with Xero connected',
      'Review the automated comparison results',
      'Check flagged items and their suggested resolutions',
    ],
    tips: [
      'Run reconciliation at the end of each quarter before BAS lodgement',
      'Focus on "error" status items first, as warnings can usually wait until later',
      'Use the TB Balance check to quickly verify if the Trial Balance is in balance',
    ],
    relatedSlugs: ['xero-integration', 'bas-validator', 'smart-comparison'],
  },
  {
    slug: 'smart-comparison',
    name: 'Smart Document Comparison',
    description: 'Perform year-over-year P&L and Balance Sheet comparisons with built-in materiality assessment. Visual charts highlight the biggest movers, and the AI flags significant variances that warrant investigation or discussion with clients.',
    category: 'financial-analysis',
    icon: 'GitCompare',
    minPlan: 2,
    route: '/panel/intelligence/comparison',
    highlight: 'Spot material changes before they surprise you in the audit',
    whyItMatters: [
      'Automatically compares current year to prior year financials, saving you hours of spreadsheet work',
      'Materiality assessment flags significant variances so you focus on what actually matters',
      'Visual charts highlight the biggest movers for quick identification and client communication',
      'Perfect for year-end review and audit preparation, giving you a complete picture in minutes',
    ],
    howToUse: [
      'Go to [Comparison](/panel/intelligence/comparison) from the top menu',
      'Select a client and choose P&L or Balance Sheet',
      'Click Analyse to run the comparison',
      'Review high-materiality items and their percentage changes',
    ],
    tips: [
      'Run before every client meeting to prepare talking points',
      'High materiality items (>5% of revenue) need investigation',
      'Use the chart view to quickly identify the biggest variances',
    ],
    relatedSlugs: ['reconciliation', 'xero-integration', 'client-reports'],
  },

  // ==================== Compliance & Tax ====================
  {
    slug: 'compliance-checker',
    name: 'ATO Compliance Checker',
    description: 'Check client financials against ATO benchmarks for 95+ industries to proactively manage audit risk. RoniDoc compares your clients\' key ratios against official ATO data and highlights items that fall outside acceptable ranges, giving you time to investigate and resolve issues before lodgement.',
    category: 'compliance',
    icon: 'Shield',
    minPlan: 2,
    route: '/panel/compliance/checker',
    highlight: 'Know your audit risk before the ATO does',
    whyItMatters: [
      'Compares client ratios against official ATO industry benchmarks for thorough compliance validation',
      'Identifies items that could trigger an ATO audit or review, so you can address them proactively',
      'Covers expense ratios, profit margins, and key financial indicators across all major categories',
      'Proactive compliance reduces client risk and builds trust, reinforcing your value as their adviser',
    ],
    howToUse: [
      'Go to [Compliance](/panel/compliance/checker) from the top menu',
      'Select a client (must have Xero connected and industry set)',
      'Review the benchmark comparison results',
      'Focus on items outside the ATO benchmark range',
    ],
    tips: [
      'Set the industry correctly for each client, because benchmarks vary significantly between sectors',
      'Items just outside the benchmark range warrant a review but aren\'t necessarily wrong',
      'Download the compliance report for client files and audit preparation',
    ],
    relatedSlugs: ['audit-risk', 'ato-benchmarks', 'bas-validator'],
  },
  {
    slug: 'audit-risk',
    name: 'ATO Audit Risk Predictor',
    description: 'Get AI-powered audit risk scoring based on financial patterns and ATO focus areas. RoniDoc analyses multiple risk factors that the ATO uses to select entities for review and delivers a clear risk score with actionable recommendations to reduce exposure.',
    category: 'compliance',
    icon: 'ShieldAlert',
    minPlan: 3,
    route: '/panel/compliance/audit-risk',
    highlight: 'Predict audit risk before lodgement and protect your clients proactively',
    whyItMatters: [
      'Analyses multiple risk factors the ATO uses to select entities for review',
      'Considers benchmark deviations, lodgement history, and industry risk for a comprehensive assessment',
      'Provides a clear risk score with actionable recommendations to reduce exposure',
      'Helps prioritise which clients need extra attention at tax time, so you allocate resources wisely',
    ],
    howToUse: [
      'Navigate to [Audit Risk](/panel/compliance/audit-risk) from the top menu',
      'Select a client to see their audit risk profile',
      'Review risk factors and their individual scores',
      'Follow the AI recommendations to reduce risk',
    ],
    tips: [
      'Run audit risk checks before every tax return lodgement',
      'Address high-risk items proactively rather than waiting for the ATO to ask questions',
      'Document your reasoning for items outside benchmarks in the client file',
    ],
    relatedSlugs: ['compliance-checker', 'tax-planning', 'deadlines'],
  },
  {
    slug: 'bas-validator',
    name: 'BAS Validator',
    description: 'Validate BAS labels against Xero data to catch errors before lodgement. The validator cross-checks every label against actual transactions, identifies common errors like wrong GST codes, and flags inconsistencies so you can fix them before submission.',
    category: 'compliance',
    icon: 'FileCheck',
    minPlan: 2,
    route: '/panel/compliance/bas',
    highlight: 'Never lodge a BAS with preventable errors again',
    whyItMatters: [
      'Cross-checks BAS labels against actual Xero transactions for complete validation',
      'Identifies common errors including wrong GST codes, missing transactions, and rounding issues',
      'Validates PAYG withholding and instalment amounts against source data',
      'Reduces BAS amendment rates and ATO correspondence, saving time and avoiding penalties',
    ],
    howToUse: [
      'Go to [BAS Validator](/panel/compliance/bas) from the top menu',
      'Select the client and quarter',
      'Review the validation results',
      'Fix any flagged issues in Xero before lodging',
    ],
    tips: [
      'Run the validator the day before lodgement as a final check',
      'Pay special attention to GST-free items, as these are the most common BAS errors',
      'Use quarterly to build confidence in your BAS preparation process',
    ],
    relatedSlugs: ['compliance-checker', 'reconciliation', 'deadlines'],
  },
  {
    slug: 'tax-planning',
    name: 'Year-End Tax Planning',
    description: 'Generate AI-powered tax planning strategies with estimated savings and deadlines tailored to each client. RoniDoc analyses your client\'s financials and suggests specific, actionable strategies covering instant asset write-off, prepayments, super contributions, and more.',
    category: 'compliance',
    icon: 'CalendarDays',
    minPlan: 3,
    route: '/panel/compliance/tax-planning',
    highlight: 'Turn tax planning from guesswork into a data-driven strategy',
    whyItMatters: [
      'AI analyses client financials to suggest specific tax-saving strategies backed by real numbers',
      'Each strategy includes estimated dollar savings so you can prioritise effectively',
      'The system is deadline-aware and highlights time-sensitive opportunities that need action soon',
      'Covers instant asset write-off, prepayments, super contributions, and more for comprehensive planning',
    ],
    howToUse: [
      'Navigate to [Tax Planning](/panel/compliance/tax-planning) from the top menu',
      'Select a client',
      'Review the AI-generated strategies',
      'Discuss recommendations with the client and implement before deadlines',
    ],
    tips: [
      'Run tax planning reports in April or May, well before 30 June, for maximum impact',
      'Focus on strategies where the estimated savings justify the implementation effort',
      'Save the report to the client file for future reference and accountability',
    ],
    relatedSlugs: ['tax-opportunities', 'what-if-scenarios', 'deadlines'],
  },
  {
    slug: 'tax-opportunities',
    name: 'Tax Opportunity Scanner',
    description: 'Scan all clients for missed tax-saving opportunities that could be putting money back in their pockets. RoniDoc\'s scanner analyses financial data across multiple tax areas simultaneously and estimates the dollar value of each opportunity, so you know exactly where to focus.',
    category: 'compliance',
    icon: 'Zap',
    minPlan: 3,
    route: '/panel/compliance/opportunities',
    highlight: 'Find money your clients are leaving on the table',
    whyItMatters: [
      'Scans financial data for commonly missed deductions and concessions across your client base',
      'Identifies opportunities across multiple tax areas simultaneously for comprehensive coverage',
      'Estimates dollar value of each opportunity for easy prioritisation and client communication',
      'Helps demonstrate value to clients by showing them exactly how much you saved them',
    ],
    howToUse: [
      'Go to [Tax Opportunities](/panel/compliance/opportunities) from the top menu',
      'Select a client or scan all clients',
      'Review identified opportunities ranked by estimated value',
      'Click each opportunity for detailed analysis and implementation steps',
    ],
    tips: [
      'Run the scanner at the start of tax season to plan your approach',
      'Use the estimated values to prioritise which clients to focus on first',
      'Share the opportunity report with clients to reinforce your value',
    ],
    relatedSlugs: ['tax-planning', 'compliance-checker', 'multi-client-query'],
  },
  {
    slug: 'what-if-scenarios',
    name: 'What-If Scenario Modelling',
    description: 'Model tax scenarios to answer questions like "what if the client buys an asset, hires staff, or restructures?" RoniDoc uses actual client financials as the baseline and calculates the real tax and cash flow impact, so your advice is grounded in data rather than guesswork.',
    category: 'ai-tools',
    icon: 'Calculator',
    minPlan: 3,
    route: '/panel/compliance/scenarios',
    highlight: 'Answer "what if" questions with real numbers, not guesses',
    whyItMatters: [
      'Model the tax impact of business decisions before they happen, giving clients clarity and confidence',
      'Compare multiple scenarios side by side to present options clearly',
      'Uses actual client financials as the baseline for realistic, trustworthy projections',
      'Helps clients make informed decisions with confidence, strengthening your advisory relationship',
    ],
    howToUse: [
      'Navigate to [What-If](/panel/compliance/scenarios) from the top menu',
      'Select a client',
      'Choose a scenario type or create a custom one',
      'Adjust parameters and see the impact on tax and cash flow',
    ],
    tips: [
      'Use before major client decisions, such as asset purchases, hiring, or restructuring',
      'Compare at least 2-3 scenarios to give clients meaningful options',
      'Save scenarios for reference during implementation',
    ],
    relatedSlugs: ['tax-planning', 'cashflow-forecast', 'client-chat'],
  },
  {
    slug: 'failure-warning',
    name: 'Business Failure Early Warning',
    description: 'RoniDoc\'s AI monitors financial indicators to detect early signs of business distress across your entire client portfolio. By flagging declining revenue, shrinking margins, and cash flow concerns early, you have time to intervene and help clients course-correct before it is too late.',
    category: 'ai-tools',
    icon: 'AlertOctagon',
    minPlan: 3,
    route: '/panel/intelligence/failure-warning',
    highlight: 'Spot trouble before your clients do and help them course-correct',
    whyItMatters: [
      'Monitors key financial health indicators across all connected clients continuously',
      'Flags declining revenue, shrinking margins, and cash flow concerns with clear severity levels',
      'Early detection gives time to intervene and prevent business failure, protecting your clients and your practice',
      'Demonstrates duty of care and proactive advisory value, setting you apart from reactive competitors',
    ],
    howToUse: [
      'Go to [Risk Warning](/panel/intelligence/failure-warning) from the top menu',
      'Review the risk-scored client list',
      'Click any client to see detailed risk factors',
      'Take action on high-risk clients immediately',
    ],
    tips: [
      'Check weekly during economic downturns for emerging patterns',
      'A declining score over 3 consecutive months is a red flag that warrants a conversation',
      'Use the risk report to have proactive conversations with at-risk clients',
    ],
    relatedSlugs: ['firm-dashboard', 'cashflow-forecast', 'client-management'],
  },

  // ==================== Automation ====================
  {
    slug: 'deadlines',
    name: 'Deadline Tracker',
    description: 'Track ATO lodgement deadlines, BAS due dates, and custom reminders in one unified calendar. RoniDoc comes pre-loaded with all standard ATO deadlines and lets you add engagement-specific dates, so nothing falls through the cracks and you avoid costly late penalties.',
    category: 'automation',
    icon: 'CalendarDays',
    minPlan: 2,
    route: '/panel/deadlines',
    highlight: 'Never miss a deadline again, because ATO penalties are expensive',
    whyItMatters: [
      'Pre-loaded with all ATO lodgement deadlines so you are covered from day one',
      'Tracks per-client deadlines with status (pending, overdue, completed) for full visibility',
      'Visual calendar view and list view with overdue highlighting make it easy to prioritise',
      'Reduces the risk of late lodgement penalties, protecting both you and your clients',
    ],
    howToUse: [
      'Navigate to [Deadlines](/panel/deadlines) from the top menu',
      'Review upcoming and overdue deadlines',
      'Click any deadline to see details and assigned client',
      'Mark deadlines as complete when lodgement is done',
    ],
    tips: [
      'Set up custom deadlines for engagement-specific dates',
      'Review overdue items weekly as part of your practice management routine',
      'Use the days-overdue count to prioritise urgent items',
    ],
    relatedSlugs: ['bas-validator', 'compliance-checker', 'workflows'],
  },
  {
    slug: 'workflows',
    name: 'Workflow Templates',
    description: 'Standardised checklists for year-end, tax returns, BAS, and onboarding that ensure consistent quality across every engagement. Pre-built templates guide your team step by step through complex processes, and completion tracking keeps everyone accountable.',
    category: 'automation',
    icon: 'CheckCheck',
    minPlan: 3,
    route: '/panel/compliance/workflows',
    highlight: 'Consistent quality across every engagement, every time',
    whyItMatters: [
      'Pre-built templates for common accounting workflows that save hours of process design',
      'Ensures nothing is missed during complex processes, even for junior staff members',
      'Standardises quality across your team so clients receive consistent service',
      'Tracks completion percentage per workflow for real-time progress visibility',
    ],
    howToUse: [
      'Go to [Workflows](/panel/compliance/workflows) from the top menu',
      'Select a template (Year-End, Tax Return, BAS, etc.)',
      'Assign to a client or team member',
      'Check off items as you complete each step',
    ],
    tips: [
      'Customise templates to match your firm\'s specific processes',
      'Use for training new staff, since workflows serve as step-by-step guides',
      'Review completion rates to identify bottlenecks in your processes',
    ],
    relatedSlugs: ['deadlines', 'reminders', 'firm-dashboard'],
  },
  {
    slug: 'reminders',
    name: 'Smart Reminders',
    description: 'Automated reminders for deadlines, follow-ups, and recurring tasks that keep your practice running smoothly. RoniDoc handles the mental overhead of remembering what needs to happen and when, so you can focus on the work itself rather than managing a to-do list.',
    category: 'automation',
    icon: 'AlertTriangle',
    minPlan: 2,
    route: '/panel/compliance/reminders',
    highlight: 'Let AI remember what you need to do, so you can focus on doing it',
    whyItMatters: [
      'Automated reminders fire before deadlines arrive, giving you lead time to prepare',
      'Follow-up reminders for client document requests ensure nothing gets forgotten',
      'Recurring reminders for quarterly and annual tasks keep your practice on schedule',
      'Reduces mental load and prevents things falling through the cracks, especially during busy season',
    ],
    howToUse: [
      'Navigate to [Reminders](/panel/compliance/reminders) from the top menu',
      'Create reminders with specific dates and descriptions',
      'Assign to yourself or team members',
      'Review and dismiss reminders as tasks are completed',
    ],
    tips: [
      'Set reminders 2 weeks before BAS due dates to allow preparation time',
      'Use recurring reminders for monthly reconciliation tasks',
      'Link reminders to specific clients for context when they trigger',
    ],
    relatedSlugs: ['deadlines', 'workflows', 'firm-dashboard'],
  },

  // ==================== Reports ====================
  {
    slug: 'client-reports',
    name: 'Auto-Generated Client Reports',
    description: 'Generate professional Word documents for monthly, quarterly, yearly, and compliance reports in seconds. RoniDoc pulls live data from Xero and compliance analysis to produce polished, client-ready reports with consistent branding and professional presentation.',
    category: 'reports',
    icon: 'FileText',
    minPlan: 2,
    route: '/panel/reports',
    highlight: 'Professional reports in 30 seconds, not 3 hours',
    whyItMatters: [
      '8 report templates covering all common reporting needs for accounting firms',
      'Pulls live data from Xero and compliance analysis so reports are always current',
      'Downloads as formatted Word documents ready for client delivery without further editing',
      'Consistent branding and professional presentation across all reports reinforces your firm\'s image',
    ],
    howToUse: [
      'Go to [Reports](/panel/reports) from the top menu',
      'Select a client',
      'Choose a report type (Monthly, Quarterly, Year-End, Compliance, etc.)',
      'Click Generate and the report downloads as a Word document',
    ],
    tips: [
      'Generate monthly management reports before every client meeting',
      'The Year-End Summary is perfect for annual review conversations',
      'Compliance Risk Reports help demonstrate advisory value to clients',
    ],
    relatedSlugs: ['firm-dashboard', 'compliance-checker', 'smart-comparison'],
  },

  // ==================== Integrations ====================
  {
    slug: 'email-integration',
    name: 'Email Integration',
    description: 'Connect Gmail, Outlook, or SMTP and let RoniDoc\'s AI classify and summarise your emails automatically. Every message is analysed for urgency, category, and sentiment, and you can create tasks directly from emails with full context preserved.',
    category: 'integrations',
    icon: 'Mail',
    minPlan: 1,
    highlight: 'Your inbox, AI-organised and prioritised',
    whyItMatters: [
      'Connect Gmail, Outlook/Office 365, or any SMTP/IMAP email provider in minutes',
      'AI classifies emails by urgency, category, and sentiment for instant prioritisation',
      'One-click summaries extract key points, actions, and deadlines from long email threads',
      'Create tasks directly from emails with full context preserved, so nothing gets lost',
    ],
    howToUse: [
      'Open the AI Business Assistant App on your mobile device',
      'Go to the Email section',
      'Connect your email provider (Gmail, Outlook, or SMTP)',
      'Browse your inbox with AI-powered classification and summaries',
    ],
    tips: [
      'Set up business context in email preferences for better AI classification',
      'Use the "Add to Todo" button to turn emails into actionable tasks',
      'Configure priority senders so important emails are always highlighted',
    ],
    relatedSlugs: ['todo-management', 'calendar-integration', 'ai-chat'],
  },
  {
    slug: 'calendar-integration',
    name: 'Google Calendar',
    description: 'View and create calendar events alongside your ATO deadlines, with voice commands for hands-free scheduling. RoniDoc merges your Google Calendar with compliance deadlines into a single unified timeline, so you never miss what is coming up next.',
    category: 'integrations',
    icon: 'CalendarDays',
    minPlan: 1,
    highlight: 'Your calendar and deadlines in one view',
    whyItMatters: [
      'View Google Calendar events alongside ATO deadlines for complete day-at-a-glance visibility',
      'Create events using voice commands or text for maximum flexibility',
      'See overdue deadlines with days-past-due count so you can act on the most urgent items first',
      'Unified view of what\'s coming up across all your commitments keeps you prepared and organised',
    ],
    howToUse: [
      'Open the AI Business Assistant App and go to Calendar',
      'Connect your Google Calendar account',
      'View events and deadlines in a combined timeline',
      'Use voice commands: "Schedule a meeting with John next Tuesday at 2pm"',
    ],
    tips: [
      'Use voice commands in the AI Business Assistant App for hands-free event creation',
      'Check the calendar view daily to stay on top of upcoming deadlines',
      'Overdue items show the exact number of days past due for prioritisation',
    ],
    relatedSlugs: ['deadlines', 'email-integration', 'todo-management'],
  },
  {
    slug: 'todo-management',
    name: 'Task Management',
    description: 'Full task management with team delegation, priorities, and email-linked tasks for seamless workflow. Create tasks from emails with one click, assign them to team members, and track progress across personal, delegated, and team views.',
    category: 'integrations',
    icon: 'CheckCheck',
    minPlan: 1,
    highlight: 'From email to action item in one click',
    whyItMatters: [
      'Create tasks from emails with full source context preserved, so you never lose the original thread',
      'Assign tasks to team members with priorities and due dates for clear accountability',
      'Track My Tasks, Delegated, and Team views for complete visibility across your practice',
      'Overdue indicators help prioritise what needs attention, keeping your team focused on what matters',
    ],
    howToUse: [
      'Open the AI Business Assistant App and go to Tasks',
      'Create tasks manually or from emails using the "+ToDo" button',
      'Assign to team members and set priorities',
      'Track progress across My Tasks, Delegated, and Team tabs',
    ],
    tips: [
      'Use the email "Add to Todo" button to preserve email context in tasks',
      'Set priorities (urgent/high/medium/low) to sort your task list effectively',
      'Review delegated tasks weekly to follow up with team members',
    ],
    relatedSlugs: ['email-integration', 'calendar-integration', 'deadlines'],
  },

  // ==================== Additional Compliance Tools ====================
  {
    slug: 'div7a-checker',
    name: 'Division 7A Checker',
    description: 'Identify potential Division 7A issues in Balance Sheets by scanning for loans to associates and related parties. Division 7A is one of the most common ATO compliance issues for private companies, and early detection gives you time to put compliant loan agreements in place.',
    category: 'compliance',
    icon: 'Building2',
    minPlan: 2,
    route: '/panel/compliance/div7a',
    highlight: 'Div 7A is the ATO\'s favourite target, so make sure you\'re compliant',
    whyItMatters: [
      'Scans Balance Sheets for loans, advances, and payments that could trigger Div 7A assessments',
      'One of the most common ATO compliance issues for private companies, making this tool essential',
      'Early detection allows time to put compliant loan agreements in place before the ATO intervenes',
      'Protects clients from unexpected deemed dividend assessments that can be financially devastating',
    ],
    howToUse: [
      'Go to [Div 7A Checker](/panel/compliance/div7a) from the top menu',
      'Select a company client with Xero connected',
      'Review flagged items including loans to directors, shareholders, and associates',
      'Follow the recommendations to establish compliant arrangements',
    ],
    tips: [
      'Run before 30 June each year to identify issues before year-end',
      'Pay attention to director loan accounts, as these are the most common trigger',
      'A compliant Div 7A loan agreement can convert a deemed dividend into a structured repayment',
    ],
    relatedSlugs: ['compliance-checker', 'audit-risk', 'trust-distribution'],
  },
  {
    slug: 'trust-distribution',
    name: 'Trust Distribution Planner',
    description: 'Plan trust distributions with tax-optimised allocation across beneficiaries using intelligent modelling. Compare different distribution scenarios, calculate the tax impact for each beneficiary including marginal rates, and ensure compliance with trust deed and tax law requirements.',
    category: 'compliance',
    icon: 'Scale',
    minPlan: 3,
    route: '/panel/compliance/trust-distribution',
    highlight: 'Optimise trust distributions before 30 June, not after',
    whyItMatters: [
      'Models different distribution scenarios across beneficiaries so you can find the optimal split',
      'Calculates tax impact for each beneficiary including marginal rates for precise planning',
      'Considers trust streaming rules for capital gains and franked dividends to maximise tax efficiency',
      'Ensures compliance with trust deed and tax law requirements, reducing the risk of costly mistakes',
    ],
    howToUse: [
      'Navigate to [Trust Distribution](/panel/compliance/trust-distribution) from the top menu',
      'Enter the trust\'s distributable income',
      'Add beneficiaries with their individual tax situations',
      'Compare different allocation scenarios',
    ],
    tips: [
      'Run distribution planning in May/June to allow implementation time',
      'Consider the 45-day holding period rule for franked dividends',
      'Document the trustee resolution before 30 June',
    ],
    relatedSlugs: ['tax-planning', 'cgt-calculator', 'div7a-checker'],
  },
  {
    slug: 'cgt-calculator',
    name: 'CGT Calculator',
    description: 'Calculate Capital Gains Tax with the discount method, indexation, and small business concessions all in one place. The calculator handles complex multi-method CGT scenarios and applies eligible concessions automatically, reducing manual calculation errors and saving valuable time.',
    category: 'compliance',
    icon: 'Calculator',
    minPlan: 3,
    route: '/panel/compliance/cgt',
    highlight: 'CGT calculations made simple, including small business concessions',
    whyItMatters: [
      'Handles complex CGT calculations with multiple methods for accurate results',
      'Applies the 50% CGT discount for assets held over 12 months automatically',
      'Considers small business CGT concessions (lifetime exemption, rollover, etc.) to minimise tax',
      'Reduces manual calculation errors in complex capital gains scenarios, giving you confidence in your figures',
    ],
    howToUse: [
      'Go to [CGT Calculator](/panel/compliance/cgt) from the top menu',
      'Enter the asset details: purchase date, cost base, sale price',
      'Select applicable concessions',
      'Review the calculated capital gain and tax impact',
    ],
    tips: [
      'Always check eligibility for the 50% discount first, because the holding period matters',
      'Small business concessions can reduce or eliminate the gain entirely, so check all four',
      'Keep records of cost base adjustments for future reference',
    ],
    relatedSlugs: ['trust-distribution', 'tax-planning', 'what-if-scenarios'],
  },
  {
    slug: 'cashflow-forecast',
    name: 'Cash Flow Forecasting',
    description: 'Get AI-powered cash flow predictions based on historical Xero data, with visual forecasts that highlight potential shortfalls before they happen. RoniDoc analyses historical patterns to project future cash flow, helping clients plan for seasonal fluctuations and make better business decisions.',
    category: 'ai-tools',
    icon: 'BarChart3',
    minPlan: 3,
    route: '/panel/compliance/cashflow',
    highlight: 'See into the financial future before cash flow becomes a crisis',
    whyItMatters: [
      'Predicts future cash flow based on historical patterns for data-driven planning',
      'Identifies potential cash shortfalls before they happen, giving clients time to prepare',
      'Helps clients plan for seasonal fluctuations with clear visual projections',
      'Supports better business decision-making with data-driven forecasts rather than gut feelings',
    ],
    howToUse: [
      'Navigate to [Cash Flow](/panel/compliance/cashflow) from the top menu',
      'Select a client with Xero connected',
      'Review the forecast chart and key indicators',
      'Identify months where intervention may be needed',
    ],
    tips: [
      'Cash flow forecasts are most useful for clients with seasonal businesses',
      'Review forecasts quarterly and adjust for known future events',
      'Use alongside scenario modelling for comprehensive "what-if" cash flow analysis',
    ],
    relatedSlugs: ['failure-warning', 'what-if-scenarios', 'xero-integration'],
  },
  {
    slug: 'super-compliance',
    name: 'Superannuation Compliance',
    description: 'Track SG obligations, contribution caps, and lodgement deadlines to ensure every client meets their superannuation requirements. The SG charge for late payment is harsh and not tax-deductible, making prevention far more cost-effective than remediation.',
    category: 'compliance',
    icon: 'Shield',
    minPlan: 3,
    route: '/panel/compliance/super',
    highlight: 'SG compliance is non-negotiable, so make sure every client is covered',
    whyItMatters: [
      'Tracks Superannuation Guarantee obligations per client for complete visibility',
      'Monitors contribution caps for directors and key employees to avoid excess contributions',
      'Alerts on approaching SG lodgement deadlines so you never miss a payment window',
      'The SG charge for late payment is harsh and not tax-deductible, making prevention essential',
    ],
    howToUse: [
      'Go to [Super Compliance](/panel/compliance/super) from the top menu',
      'Select a client with payroll data in Xero',
      'Review SG obligations and compliance status',
      'Check for any late or missing payments',
    ],
    tips: [
      'SG must be paid by the 28th of the month after the quarter ends',
      'Late SG payments attract the SG Charge, which is not tax-deductible',
      'Check quarterly to ensure clients are meeting their obligations',
    ],
    relatedSlugs: ['compliance-checker', 'deadlines', 'bas-validator'],
  },
  {
    slug: 'fbt-detector',
    name: 'FBT Detector',
    description: 'Scan expense accounts for potential Fringe Benefits Tax obligations that many businesses miss until an audit. RoniDoc\'s AI identifies car parking, entertainment, loans, and other common FBT triggers, giving you time to restructure or elect exemptions before the FBT year-end.',
    category: 'compliance',
    icon: 'AlertOctagon',
    minPlan: 3,
    route: '/panel/compliance/fbt',
    highlight: 'FBT lurks in the most unexpected places, so let AI find it',
    whyItMatters: [
      'Scans expense accounts for items that may attract FBT, catching what manual reviews often miss',
      'Identifies car parking, entertainment, loans, and other common FBT triggers automatically',
      'Many businesses don\'t realise they have FBT obligations until an audit, making early detection critical',
      'Early detection allows time to restructure or elect exemptions before the FBT year-end deadline',
    ],
    howToUse: [
      'Navigate to [FBT Detector](/panel/compliance/fbt) from the top menu',
      'Select a client with Xero connected',
      'Review flagged expense categories',
      'Assess each item for FBT applicability',
    ],
    tips: [
      'Run the FBT detector before 31 March each year (FBT year-end)',
      'Car parking near work premises is one of the most commonly missed FBT items',
      'Small businesses may be exempt from car parking FBT, so check the turnover threshold',
    ],
    relatedSlugs: ['compliance-checker', 'tax-planning', 'audit-risk'],
  },
  {
    slug: 'missing-docs',
    name: 'Missing Document Detector',
    description: 'Identify which documents are missing from client files for a complete engagement. RoniDoc automatically checks each client against the expected document list and shows you exactly what is still outstanding, reducing endless back-and-forth email requests.',
    category: 'automation',
    icon: 'FileSearch',
    minPlan: 2,
    route: '/panel/compliance/missing-documents',
    highlight: 'Stop chasing documents and know exactly what\'s missing',
    whyItMatters: [
      'Automatically checks which standard documents are missing per client for complete awareness',
      'Covers tax returns, financial statements, identity documents, and more',
      'Reduces back-and-forth email requests with clients, saving time for everyone involved',
      'Ensures engagement files are complete before lodgement, preventing last-minute scrambles',
    ],
    howToUse: [
      'Go to [Missing Docs](/panel/compliance/missing-documents) from the top menu',
      'Select a client or view all clients',
      'Review the checklist of expected vs. received documents',
      'Send document request emails directly from the interface',
    ],
    tips: [
      'Run at the start of each engagement to identify what you need upfront',
      'Customise the expected document list per engagement type',
      'Use with the client portal to let clients upload directly',
    ],
    relatedSlugs: ['document-upload', 'workflows', 'client-management'],
  },

  // ==================== Security ====================
  {
    slug: 'encryption',
    name: 'AES-256 Encryption',
    description: 'All sensitive data is encrypted at rest with AES-256 military-grade encryption. RoniDoc uses per-record initialisation vectors and stores encryption keys separately from encrypted data, ensuring that even a database compromise would not expose your information.',
    category: 'security',
    icon: 'Lock',
    minPlan: 0,
    highlight: 'Bank-level security for your most sensitive data',
    whyItMatters: [
      'All OAuth tokens, API keys, and sensitive credentials are encrypted at rest using industry-standard algorithms',
      'AES-256-CBC encryption with per-record initialisation vectors provides maximum protection',
      'Even if the database is compromised, encrypted data remains unreadable without the separate key store',
      'Meets requirements for Australian privacy legislation compliance, so you can assure your clients',
    ],
    howToUse: [
      'Encryption is automatic and requires no configuration on your part',
      'All data is encrypted before storage and decrypted on access transparently',
      'Encryption keys are stored separately from encrypted data for defence in depth',
    ],
    tips: [
      'Encryption is always on and cannot be disabled, ensuring consistent protection',
      'Regular key rotation is handled by the platform automatically',
    ],
    relatedSlugs: ['tenant-isolation', 'audit-trail'],
  },
  {
    slug: 'tenant-isolation',
    name: 'Tenant Isolation',
    description: 'Complete data isolation between organisations using PostgreSQL Row-Level Security. Every database query is automatically filtered by tenant, and even application-level bugs cannot expose cross-tenant data, giving you enterprise-grade protection out of the box.',
    category: 'security',
    icon: 'Shield',
    minPlan: 0,
    highlight: 'Your data is yours, completely isolated from other firms',
    whyItMatters: [
      'PostgreSQL Row-Level Security ensures tenants can never access each other\'s data under any circumstances',
      'Every database query is automatically filtered by tenant for seamless, invisible protection',
      'Even application bugs cannot expose cross-tenant data, because the security is enforced at the database layer',
      'Critical for accounting firms handling sensitive client information that must remain confidential',
    ],
    howToUse: [
      'Tenant isolation is automatic and built into the database layer from the ground up',
      'No configuration is needed, as it is enforced at every query automatically',
    ],
    tips: [
      'Each firm gets their own isolated data space within the shared infrastructure',
      'This is the same approach used by enterprise SaaS platforms like Salesforce',
    ],
    relatedSlugs: ['encryption', 'audit-trail'],
  },
  {
    slug: 'audit-trail',
    name: 'Audit Trail',
    description: 'A complete activity log showing who did what, when, and from where. Every significant action is recorded with timestamps and IP addresses, providing the accountability and traceability that compliance frameworks and professional standards require.',
    category: 'security',
    icon: 'ClipboardList',
    minPlan: 3,
    route: '/panel/audit-log',
    highlight: 'Complete accountability, because every action is recorded',
    whyItMatters: [
      'Logs every significant action including logins, data access, AI queries, and file uploads',
      'Records IP addresses and timestamps for security investigations and forensic analysis',
      'Required for many compliance frameworks and professional standards, keeping your firm audit-ready',
      'Helps identify unusual activity patterns that could indicate security issues or unauthorised access',
    ],
    howToUse: [
      'Go to Settings > [Audit Log](/panel/audit-log)',
      'Browse recent activity or search for specific actions',
      'Filter by user, action type, or date range',
      'Export logs for compliance reporting',
    ],
    tips: [
      'Review audit logs monthly for unusual patterns',
      'Check for logins from unexpected locations',
      'Keep exports for your firm\'s compliance records',
    ],
    relatedSlugs: ['encryption', 'tenant-isolation'],
  },

  // ==================== Additional features ====================
  {
    slug: 'ato-benchmarks',
    name: 'ATO Industry Benchmarks',
    description: 'Access 95+ industry benchmarks sourced directly from the ATO, fully integrated into RoniDoc\'s AI analysis layer. These benchmarks are automatically referenced whenever the AI analyses client financials, giving you instant insight into how each client compares to their industry peers.',
    category: 'getting-started',
    icon: 'BookOpen',
    minPlan: 0,
    route: '/panel/ato-knowledge',
    highlight: 'The ATO\'s own data, working for you',
    whyItMatters: [
      'Official ATO benchmark data for 95+ industries, loaded and ready to use from day one',
      'Automatically referenced when AI analyses client financials for context-aware insights',
      'Compare expense ratios, profit margins, and key indicators against industry peers effortlessly',
      'Essential for audit risk assessment and compliance checking, forming the backbone of proactive advisory',
    ],
    howToUse: [
      'ATO benchmarks are automatically used by AI in client chats with no extra steps required',
      'Browse the [ATO Knowledge Base](/panel/ato-knowledge) to view benchmark data directly',
      'Ask the AI: "How does this client compare to industry benchmarks?"',
    ],
    tips: [
      'Mention "benchmark" or "ATO" in your chat questions to trigger benchmark analysis',
      'Benchmarks are most useful when the client\'s industry is correctly set',
      'Use benchmarks proactively in client meetings to demonstrate advisory value',
    ],
    relatedSlugs: ['compliance-checker', 'audit-risk', 'global-kb'],
  },

  // ==================== SMSF ====================
  {
    slug: 'smsf-dashboard',
    name: 'SMSF Dashboard',
    description: 'Firm-wide overview of all Self-Managed Super Funds under management. View total assets, member counts, compliance scores, upcoming actions, and fund performance at a glance.',
    category: 'smsf',
    icon: 'Shield',
    minPlan: 3,
    route: '/panel/smsf',
    highlight: 'Your command centre for managing every SMSF in the practice',
    whyItMatters: [
      'Instantly see the health of your entire SMSF portfolio - compliance status, lodgement due dates, and asset totals',
      'Spot problems before they become breaches: expiring BDBNs, overdue valuations, minimum pension shortfalls',
      'Track total assets under management across all funds for practice reporting',
      'Prioritise your workflow by seeing which funds need attention first',
    ],
    howToUse: [
      'Navigate to [SMSF Dashboard](/panel/smsf) from the SMSF menu in the top navigation',
      'Review the KPI cards showing total funds, assets, members in pension, and compliance score',
      'Check the compliance table to see which funds have outstanding items (red crosses)',
      'Click on any fund name to drill into the fund detail page',
      'Use the "Urgent Items" section to prioritise your daily tasks',
    ],
    tips: [
      'Check the dashboard first thing each morning during SMSF season to stay on top of deadlines',
      'The compliance score weights trust deed, investment strategy, BDBN, lodgement, and pension compliance equally',
      'Use the quick action buttons at the bottom to jump directly to common tasks',
    ],
    relatedSlugs: ['smsf-contributions', 'smsf-compliance', 'smsf-pensions'],
  },
  {
    slug: 'smsf-fund-management',
    name: 'Fund Setup & Administration',
    description: 'Register new SMSFs, manage trustees and members, track trust deeds, death benefit nominations, investment strategies, ATO lodgements, and prepare audit packs - all in one place.',
    category: 'smsf',
    icon: 'FileText',
    minPlan: 3,
    route: '/panel/smsf/funds',
    highlight: 'Complete fund lifecycle management from registration to wind-up',
    whyItMatters: [
      'Centralise all fund administration in one system instead of spreadsheets and email folders',
      'Never miss a BDBN expiry - the 3-year rule is tracked automatically with colour-coded alerts',
      'Trust deed version history ensures you always know which deed is current and when it was last reviewed',
      'Investment strategy review reminders ensure compliance with Reg 4.09 requirements',
    ],
    howToUse: [
      'Go to [All Funds](/panel/smsf/funds) from the SMSF menu to see all registered funds',
      'Click [Register Fund](/panel/smsf/new) to create a new SMSF - the 4-step wizard covers: (1) Fund Details - name, trustee type, establishment date; (2) Members - add 1-6 members with name, DOB, TFN, email; (3) Trust Deed - upload deed, select provider; (4) Registration - ABN/TFN, bank account, electronic service address',
      '**Overview tab**: Fund summary showing ABN, TFN, establishment date, total assets, member count, contributions YTD, and compliance traffic light (green/amber/red)',
      '**Trustees tab**: Table of current and former trustees - name, type (individual/corporate), role, date appointed, declaration signed status. Add or cease trustees here',
      '**Members tab**: Member register showing name, DOB, masked TFN, preservation age, status (accumulation/pension/both), and balance. Click a member to see beneficiaries and employment details',
      '**Trust Deed tab**: Current deed version card with upload date and version number. Full version history below. "Upload New Version" button for deed updates. Shows review-due reminder if overdue',
      '**Death Benefits (BDBN) tab**: Table of binding/non-binding/lapsing nominations per member. Shows beneficiaries, date signed, expiry date, and days until expiry. Colour-coded: green (>6 months), amber (3-6 months), red (<3 months or expired)',
      '**Investment Strategy tab**: Current strategy summary with target asset allocation. Bar chart comparing target % vs actual % by asset class. Review history list. Alert if last review was >12 months ago',
      '**Lodgement tab**: SAR due dates by financial year with status badges (lodged/overdue/draft/not started). Progress tracker for the current year\'s SAR preparation',
      '**Audit Pack tab**: 8-category document checklist (Bank Reconciliation, Asset Valuations, Contribution Summary, Pension Records, Trust Deed, Investment Strategy, Minutes, Member Statements). Progress bars per category + overall completion percentage. "Generate Audit Pack" button to compile everything',
    ],
    tips: [
      'Set up the fund registration wizard with all member details upfront - it saves re-entry later',
      'Upload the signed trust deed immediately after establishment to start the version history',
      'Review BDBNs at least 6 months before expiry to give trustees time to re-sign',
      'The audit pack checklist tracks 8 document categories - aim for 100% before engaging the auditor',
    ],
    relatedSlugs: ['smsf-dashboard', 'smsf-contributions', 'smsf-compliance'],
  },
  {
    slug: 'smsf-contributions',
    name: 'Contributions & Caps',
    description: 'Track concessional and non-concessional contribution caps per member, calculate bring-forward and carry-forward amounts, check work test eligibility, estimate Division 293 tax, and manage rollovers.',
    category: 'smsf',
    icon: 'Calculator',
    minPlan: 3,
    route: '/panel/smsf/contributions',
    highlight: 'Never breach a contribution cap again - live tracking with automatic warnings',
    whyItMatters: [
      'Contribution cap breaches attract excess contributions tax at up to 47% + Medicare levy - prevention is essential',
      'The $30,000 concessional cap and $120,000 non-concessional cap (2025-26) require real-time monitoring across all super interests',
      'Carry-forward unused concessional cap (available for 5 years if TSB < $500K) creates valuable planning opportunities',
      'Bring-forward rules allow up to 3x the NCC cap ($360K) but eligibility depends on Total Super Balance at prior 30 June',
    ],
    howToUse: [
      'Open [Contributions & Caps](/panel/smsf/contributions) from the SMSF menu, then select a fund from the dropdown',
      '**Cap Tracker tab**: Per-member table showing concessional YTD vs $30K cap and non-concessional YTD vs $120K cap. Progress bars colour-coded: green (<80%), amber (80-95%), red (>95% or breached)',
      '**Bring-Forward tab**: Select a member to see their TSB, bring-forward eligibility (TSB must be < $1.9M), and model 3-year scenarios. Shows tier calculation: Tier 1 ($360K), Tier 2 ($240K), Tier 3 ($120K)',
      '**Carry-Forward tab**: 5-year history per member of unused concessional cap amounts. Shows cumulative available amount. Only eligible if TSB < $500K at prior 30 June',
      '**Work Test tab**: Per-member table showing age, employment status, hours worked, and work test result (Met/Exempt/Not Met). Highlights members aged 67-74 who need the 40-hour/30-day test',
      '**Allocations tab**: Per-member breakdown by contribution type (salary sacrifice, employer SG, personal deductible, non-concessional, downsizer, spouse, co-contribution). Stacked bar chart visualisation',
      '**Div 293 tab**: Calculator showing member income + concessional contributions vs $250K threshold. Calculates the additional 15% tax payable. Table with results per member',
      '**Rollovers tab**: Table of rollover transactions in/out - date, type, from/to fund, amount, SuperStream reference, status badge',
    ],
    tips: [
      'Always check TSB at 30 June of the prior year before recommending NCC strategies - it determines bring-forward eligibility',
      'Remind members to submit their Notice of Intent to Claim (s290-170) to the fund BEFORE lodging their tax return',
      'For members aged 67-74, verify the work test (40 hours in 30 consecutive days) before accepting contributions',
      'Downsizer contributions ($300K per member for age 55+) do NOT count against NCC caps - flag these separately',
    ],
    relatedSlugs: ['smsf-pensions', 'smsf-fund-management'],
  },
  {
    slug: 'smsf-investments',
    name: 'Investment Compliance',
    description: 'Monitor sole purpose test compliance, in-house asset ratios (5% limit), related party transactions, LRBA safe harbour compliance (PCG 2016/5), asset valuations, collectables register, and NALI detection.',
    category: 'smsf',
    icon: 'ShieldAlert',
    minPlan: 3,
    route: '/panel/smsf/investments',
    highlight: 'Automated compliance scanning across all fund investments',
    whyItMatters: [
      'In-house assets exceeding 5% of total fund value is a direct contravention of s71 SIS Act - automatically monitored',
      'Sole purpose test (s62) breaches can result in the fund being made non-complying, losing the 15% concessional tax rate',
      'Related party transactions at non-arm\'s length terms trigger NALI provisions - income taxed at 45%',
      'LRBA loans must comply with PCG 2016/5 safe harbour terms or risk adverse ATO scrutiny',
    ],
    howToUse: [
      'Open [Investment Compliance](/panel/smsf/investments) from the SMSF menu and select a fund',
      '**Sole Purpose Test tab**: Per-fund checklist of sole purpose criteria with pass/fail indicators. Flags items like holiday property used by members, personal use of fund assets. Overall traffic light (green/amber/red)',
      '**In-House Assets tab**: Shows total fund assets, in-house asset value, and the IHA ratio as a gauge. Must stay under 5%. Alert banners for approaching or breaching the limit',
      '**Related Parties tab**: Flagged transactions table - date, description, parties involved, amount, risk level (high/medium/low), and action required. Covers loans to members (s65), asset acquisitions from related parties (s66)',
      '**LRBA tab**: Table of limited recourse borrowing arrangements - property/asset, lender, loan balance, interest rate, LVR, PCG 2016/5 safe harbour compliance status, next repayment due',
      '**Valuations tab**: Asset register showing type (property/shares/unlisted/collectables), last valuation date, current value, next valuation due, status (current/due soon/overdue). Alerts for overdue valuations',
      '**Collectables tab**: Register of collectables and personal use assets (art, wine, cars) with purchase date, cost, current value, storage location, insurance details, and compliance flags (stored correctly? used by members?)',
      '**NALI tab**: Scan results for Non-Arm\'s Length Income - transaction, type, parties, actual rate vs market rate comparison, NALI risk flag. Income caught by NALI is taxed at 45%',
    ],
    tips: [
      'Holiday properties used by members are a classic sole purpose test red flag - always verify usage arrangements',
      'Collectables (art, wine, jewellery) have strict storage, insurance, and usage rules under SIS Reg 13.18AA',
      'LRBA interest rates must match the Div 7A benchmark rate for related party loans',
      'Get independent property valuations for real property - don\'t rely on council rates notices alone',
    ],
    relatedSlugs: ['smsf-compliance', 'smsf-fund-management'],
  },
  {
    slug: 'smsf-pensions',
    name: 'Pensions & Benefits',
    description: 'Manage pension commencement, calculate minimum annual payments by age, track Transfer Balance Cap usage, calculate ECPI (segregated and unsegregated methods), process death benefits and commutations.',
    category: 'smsf',
    icon: 'Users',
    minPlan: 3,
    route: '/panel/smsf/pensions',
    highlight: 'Pension compliance made simple - minimum payments, TBC, and ECPI all tracked',
    whyItMatters: [
      'Failure to pay the minimum pension amount causes the pension to be deemed to have ceased - losing tax exemptions for the entire year',
      'The Transfer Balance Cap ($1.9M for 2025-26) limits how much can enter the tax-free pension phase',
      'ECPI calculation method (segregated vs unsegregated) significantly impacts fund tax and requires actuarial certificates for unsegregated',
      'Death benefit tax treatment depends on dependency status - up to 32% tax for non-dependant adult children on the taxable component',
    ],
    howToUse: [
      'Open [Pensions & Benefits](/panel/smsf/pensions) from the SMSF menu and select a fund',
      '**Pension Setup tab**: Cards for each member in pension phase showing pension type (Account-Based Pension or TRIS), commencement date, opening balance, and reversionary beneficiary. "Start New Pension" button for new commencements',
      '**Minimum Payments tab**: Table per member showing age, 1 July balance, minimum factor by age bracket (<65: 4%, 65-74: 5%, 75-79: 6%, 80-84: 7%, 85-89: 9%, 90-94: 11%, 95+: 14%), minimum amount, paid YTD, remaining, and on-track/underpaid status',
      '**Transfer Balance tab**: Per-member TBC tracker showing the $1.9M cap, amount used, remaining space, and a table of TBC events (commencements, commutations, rollovers) with dates and amounts',
      '**ECPI tab**: Calculator comparing segregated vs unsegregated methods. Inputs: total fund income, pension assets, total assets. Shows exempt percentage, exempt income, and actuarial certificate status. Recommends optimal method',
      '**Death Benefits tab**: Per-member breakdown of tax-free and taxable components. Beneficiary dependency analysis (dependant vs non-dependant) with tax impact calculations. Payment options flow chart',
      '**Commutations tab**: Calculator for partial or full commutations. Enter commutation amount to see proportional reduction in tax-free and taxable components, new balance, and impact on Transfer Balance Cap',
    ],
    tips: [
      'Set up pension payment reminders at the start of each financial year - minimum must be paid by 30 June',
      'For new pensions starting mid-year, the minimum is pro-rated based on the number of days',
      'Always check if TBAR needs to be lodged when a pension commences, is commuted, or a member dies',
      'The re-contribution strategy (withdraw + re-contribute as NCC) can increase the tax-free component - model it before recommending',
    ],
    relatedSlugs: ['smsf-contributions', 'smsf-compliance'],
  },
  {
    slug: 'smsf-reporting',
    name: 'SMSF Reporting & Audit',
    description: 'Prepare SMSF Annual Returns (SAR), generate financial statements, produce member statements, build comprehensive audit packs, track auditor exceptions, and benchmark fund performance against ATO averages.',
    category: 'smsf',
    icon: 'FileText',
    minPlan: 3,
    route: '/panel/smsf/reporting',
    highlight: 'Audit-ready reporting that saves hours of preparation time',
    whyItMatters: [
      'The SMSF Annual Return (SAR) is the key compliance document - late or incorrect lodgement can trigger ATO action',
      'A well-organised audit pack reduces audit fees and turnaround time significantly',
      'Member statements are required annually to show each member their balance, contributions, and tax components',
      'Benchmarking against ATO SMSF averages helps identify outliers in expense ratios and returns',
    ],
    howToUse: [
      'Open [Reporting & Audit](/panel/smsf/reporting) from the SMSF menu and select a fund',
      '**Annual Return (SAR) tab**: Progress tracker for SAR preparation - steps include Financial Statements, Member Statements, Tax Calculation, Schedules, and Lodgement. Per-fund status cards showing current stage',
      '**Financial Statements tab**: Preview of Operating Statement (income, expenses, net result) and Statement of Financial Position (assets, liabilities, equity). Shows actual dollar amounts from fund data',
      '**Member Statements tab**: Per-member annual statement - opening balance, contributions, earnings, fees, tax, closing balance. Tax component breakdown (tax-free, taxable-taxed, taxable-untaxed)',
      '**Audit Pack tab**: Comprehensive checklist grouped by 5 categories: Bank & Cash (bank recs, statements), Investments (share holdings, property valuations, unlisted assets), Contributions (member summaries, employer contributions), Pensions (commencement docs, payment summaries, minimum pension calc), Compliance (trust deed, investment strategy, minutes, BDBN). Progress bars per category + overall completion %',
      '**Audit Exceptions tab**: Table of auditor findings - exception description, severity (critical/major/minor), auditor comment, rectification status, due date. Track resolution progress',
      '**Benchmarks tab**: Horizontal bar chart comparing your fund vs ATO SMSF averages on expense ratio, return on assets, asset allocation, and admin costs',
    ],
    tips: [
      'Start audit pack preparation at least 2 months before the lodgement deadline',
      'Ensure all asset valuations are dated within 2 months of 30 June for the auditor',
      'Keep the audit exception register up to date - unresolved findings from prior years raise red flags',
      'Compare your fund\'s expense ratio to the ATO benchmark - outliers may indicate fee issues',
    ],
    relatedSlugs: ['smsf-compliance', 'smsf-fund-management'],
  },
  {
    slug: 'smsf-compliance',
    name: 'SMSF Compliance & Risk',
    description: 'Run comprehensive compliance checklists, AI-powered sole purpose reviews, disqualified person checks, residency tests, wind-up workflows, penalty calculations, and ATO correspondence management.',
    category: 'smsf',
    icon: 'ShieldAlert',
    minPlan: 3,
    route: '/panel/smsf/compliance',
    highlight: 'Proactive compliance management - catch issues before the auditor does',
    whyItMatters: [
      'SMSF trustees face personal liability for contraventions - penalties range from education directions to significant fines',
      'Non-complying SMSFs lose the concessional 15% tax rate - the fund\'s assets are taxed at the top marginal rate (47%)',
      'Disqualified persons (s120) acting as trustees can void the fund\'s registration entirely',
      'ATO has increased SMSF audit activity - proactive compliance reduces intervention risk',
    ],
    howToUse: [
      'Open [Compliance & Risk](/panel/smsf/compliance) from the SMSF menu and select a fund',
      '**Compliance Checklist tab**: 17 interactive checklist items across 7 categories (Trust Deed, Investment Strategy, Trustee Administration, Contributions, Pensions, Assets, Lodgements). Check items off and attach evidence documents',
      '**Sole Purpose AI tab**: AI-powered review of fund activities against s62. Shows per-fund compliance score (0-100), flagged items with explanations, and recommendations for remediation',
      '**Disqualified Persons tab**: Table of all trustees with 4 checks per person - undischarged bankrupt, disqualified by regulator, convicted of dishonesty, under 18. Status badge (Clear/Flagged)',
      '**Residency tab**: Per-fund residency test with 3 indicators - central management & control in Australia, active member test (50% threshold), asset test. Overall "Australian Resident Fund" badge or warning',
      '**Wind-Up tab**: 7-step wind-up workflow - (1) Board resolution, (2) Notify ATO, (3) Pay liabilities, (4) Dispose assets, (5) Pay benefits to members, (6) Lodge final SAR, (7) Cancel ABN. Status tracker per step',
      '**Penalties tab**: Table of 7 common SIS Act contraventions (s65 loans, s84 sole purpose, s71 in-house assets, etc.) with penalty units and dollar amounts. Interactive calculator: select contravention type to see penalty',
      '**ATO Correspondence tab**: Table of ATO letters received - date, subject, type (query/audit/assessment/direction), response due date, status (pending/responded/resolved). Upload button for new correspondence',
    ],
    tips: [
      'Run the compliance checklist quarterly, not just at year-end - catching issues early is much cheaper to fix',
      'Document all trustee decisions in formal minutes - they are your primary evidence in an audit',
      'The residency test has three components: central management & control, active member test, and asset test - all must be met',
      'If you discover a contravention, consider voluntary rectification before the audit - it significantly reduces penalties',
    ],
    relatedSlugs: ['smsf-investments', 'smsf-reporting', 'smsf-dashboard'],
  },
  {
    slug: 'smsf-advisory',
    name: 'SMSF Advisory & Strategy',
    description: 'Optimise contribution strategies, check downsizer eligibility, model re-contribution strategies for tax-free component uplift, compare ECPI segregation methods, plan estate succession, and assess LRBA property purchases.',
    category: 'smsf',
    icon: 'Zap',
    minPlan: 3,
    route: '/panel/smsf/advisory',
    highlight: 'Turn compliance work into high-value advisory conversations',
    whyItMatters: [
      'Contribution optimisation can save members thousands in tax annually - concessional contributions taxed at 15% vs up to 47% marginal rate',
      'The downsizer contribution ($300K per member, age 55+) is a powerful but under-used strategy for appropriate clients',
      'Re-contribution strategies can increase the tax-free component, saving beneficiaries significant tax on death',
      'Segregation method choice (segregated vs unsegregated ECPI) directly impacts fund tax - the wrong method can cost thousands',
    ],
    howToUse: [
      'Open [Advisory & Strategy](/panel/smsf/advisory) from the SMSF menu',
      '**Contribution Optimiser tab**: Per-member analysis showing current contributions, marginal tax rate, and recommended strategy (maximise concessional first, then NCC). Bar chart comparing current vs optimised tax burden. Dollar savings displayed prominently',
      '**Downsizer tab**: Eligibility checker with 4 criteria - age >= 55, property owned >= 10 years, principal residence, not previously used downsizer. Shows eligible amount (up to $300K per member) and result badge',
      '**Re-contribution tab**: Model the withdraw-and-recontribute strategy. Shows before/after comparison of tax-free vs taxable components. Calculates death benefit tax savings for non-dependant beneficiaries',
      '**Segregation tab**: Side-by-side comparison of segregated vs unsegregated ECPI methods. Table showing exempt %, tax saving, actuarial certificate cost, and net benefit. Recommendation card with the better option',
      '**Estate Planning tab**: Per-member summary of BDBN status, reversionary pension arrangements, and beneficiary dependency analysis. Risk flags for "BDBN expired", "no reversionary beneficiary". Action items checklist',
      '**LRBA Property tab**: 5-step LRBA setup checklist - (1) Identify property, (2) Establish bare trust, (3) Arrange loan (PCG 2016/5 terms), (4) Related party assessment, (5) Compliance checks. Status tracker (completed/in-progress/not-started)',
    ],
    tips: [
      'Present contribution optimisation results in dollar terms - "$X saved per year" resonates more than percentage comparisons',
      'Downsizer contributions are exempt from the work test and the age test - highlight this for older clients',
      'Re-contribution strategies work best for members with large taxable components and non-dependant beneficiaries',
      'Always factor in the cost of an actuarial certificate ($300-$600) when recommending the unsegregated method',
    ],
    relatedSlugs: ['smsf-contributions', 'smsf-pensions'],
  },
  {
    slug: 'smsf-knowledge',
    name: 'SMSF Knowledge & Education',
    description: 'Access the SMSF knowledge base covering ATO rulings, SIS Act provisions, legislative changes, key court cases, and trustee education modules that fulfil s157 education direction requirements.',
    category: 'smsf',
    icon: 'BookOpen',
    minPlan: 3,
    route: '/panel/smsf/knowledge',
    highlight: 'Stay current on SMSF rules with searchable rulings, case law, and education',
    whyItMatters: [
      'SMSF regulation changes frequently - missing a legislative amendment can result in non-compliance',
      'Key court cases (Bamford, Hart, Marsella) shape how rules are interpreted in practice',
      'Trustee education modules can fulfil ATO education direction requirements (s157 SIS Act)',
      'A searchable SMSF knowledge base saves research time when advising on complex issues',
    ],
    howToUse: [
      'Open [SMSF Knowledge](/panel/smsf/knowledge) from the SMSF menu',
      '**Knowledge Base tab**: Searchable list of SMSF topics organised by category (Contributions, Pensions, Investments, Compliance, Tax, Administration). Each item shows title, description, relevant SIS Act section, and last updated date. Filter by category or search by keyword',
      '**Ruling Assistant tab**: Ask questions about specific SMSF rulings in the input field. Below: 4 pre-made common queries with expandable answers - "Can my SMSF buy property from my parents?", "What is the current TBC?", "Can a member borrow from the fund?", "What is the sole purpose test?"',
      '**Legislative Alerts tab**: Timeline of recent and upcoming regulatory changes. Each item shows date, title, description, impact level (high/medium/low), and effective date. Covers TBC indexation, SG rate changes, preservation age updates, 6-member limit expansion',
      '**Case Law tab**: Table of key SMSF court cases - Commissioner of Taxation v Bamford [2010] (trust income), Marsella/Hart (sole purpose), JNVQ (LRBA), Aussiegolfa (in-house assets). Expandable rows with full relevance summary',
      '**Trustee Education tab**: 6 self-paced education modules - Trustee Responsibilities, Investment Strategy, Contribution Rules, Pension Rules, Record Keeping, Insurance. Progress bars showing completion %. "Start Module" or "Continue" buttons. Fulfils ATO s157 education direction requirements',
    ],
    tips: [
      'Subscribe to the legislative alerts - TBC indexation, SG rate changes, and cap changes happen every July',
      'The Bamford case (2010) is fundamental to trust income distribution - understand it thoroughly',
      'Trustee education modules are self-paced - clients can complete them independently and you track progress',
      'Search by SIS Act section number (e.g. "s65" for loans to members) for precise results',
    ],
    relatedSlugs: ['smsf-compliance', 'smsf-dashboard'],
  },
];

// Helper to get features by category
export function getFeaturesByCategory(): Record<FeatureCategory, Feature[]> {
  const grouped: Record<string, Feature[]> = {};
  for (const cat of Object.keys(CATEGORIES)) {
    grouped[cat] = FEATURES.filter(f => f.category === cat);
  }
  return grouped as Record<FeatureCategory, Feature[]>;
}

// Helper to get a feature by slug
export function getFeatureBySlug(slug: string): Feature | undefined {
  return FEATURES.find(f => f.slug === slug);
}
